import { CancelTokenSource } from "axios";
import ChatAPI from './chat-api';

export function getGlobal<T = string>(name: string): T {
  return (process.env?.[name] || window.SYDLE_CHAT_WEB_ENV?.[name])! as unknown as T;
}

export function setGlobal(name: string, value: string) {
  if (process && process.env) {
    process.env[name] = value;
  }
  if (window.SYDLE_CHAT_WEB_ENV) {
    window.SYDLE_CHAT_WEB_ENV[name] = value;
  }
}

export enum S1MessageType {
  ERROR = 'ERROR',
  WARNING = 'WARN',
  INFO = 'INFO',
  DEBUG = 'DEBUG'
}

export enum FeaturedLocation {
  DETAILS = "DETAILS",
  RELATIONSHIP = "RELATIONSHIP",
  TIMELINE = "TIMELINE",
  PIPELINE = "PIPELINE"
}

export enum ExecutionContext {
  OBJECT = "OBJECT",
  CLASS = "CLASS",
}

export interface CustomFileType extends File {
  isDocument?: boolean;
}

export const SD_ANONYMOUS_USER_ID = '5cbdb800dbab3e44afeae867';
export const CHAT_ANONYMOUS_USER_ID = '609e68846d5c2e645c104c12'

export interface AuthenticatorsConfig {
  allowPasswordRecovery?: boolean;
  authenticatorId: string;
  loginUrl?: string;
  getLogoutUrl?: string;
  hasLogout: boolean;
  identifier: string;
  name: string;
  type: string;
}

export interface MenuMethod {
  executionContext: Array<ExecutionContext>;
  exhibition: string;
  featuredLocation: Array<FeaturedLocation>;
  icon: string;
  identifier: string;
  inputClassId: string | null;
  maximumObjects: number | null;
  minimumObjects: number;
  name: string;
  outputClassId: string | null;
}

export interface S1Message {
  code: string;
  context: string;
  text: string;
  type: S1MessageType
}

export interface APIErrorResponse {
  contextMessages: S1Message[],
  generalMessages: S1Message[],
  _messages: S1Message[]
}

export interface InfoCardFeatures {
  label: string,
  value: string[],
}

export interface InfoCardLogo {
  classIdentifier: string,
  classPackageIdentifier: string,
  fileId: string,
  objectId: string,
  _classId: string,
}

export interface InfoCard {
  _id: string,
  features: InfoCardFeatures[],
  logo: InfoCardLogo | null,
  type: string,
  canGet: boolean,
  ids: string[],
}

export interface FileBean {
  contentType?: string;
  _id: string;
  _objId?: string;
  _classId?: string;
  collection?: string;
  hash?: string;
  length?: number;
  name?: string;
  storage?: string;
}

export interface AclUser {
  _id: string;
  name: string;
  photo: FileBean | null;
}

export interface CommunicationTool {
  number?: string;
  _id?: string;
  user?: AclUser;
  photo?: FileBean | null;
}

export interface Client {
  _id: string;
  name: string;
  photo: FileBean | null;
}

export interface Chat {
  _id: string;
  _class?: any;
  client: Client | null;
  communicationTool: CommunicationTool | null;
  processInstance?: {
    _id: string,
    _classId: string,
    classIdentifier: string,
    classPackageIdentifier: string
  };
  status: 'new' | 'inprogress' | 'completed';
}

export interface KeyboardButton {
  type: 'text' | 'link';
  text: string;
  identifier?: string;
}

export interface CustomKeyboard {
  buttons: KeyboardButton[];
}

export enum MessageType {
  Audio = 'audio',
  Contacts = 'contacts',
  Document = 'document',
  Hsm = 'hsm',
  Image = 'image',
  Location = 'location',
  Text = 'text',
  Video = 'video',
  VideoConference = 'videoConference',
  Sticker = 'sticker',
  Internal = 'internal',
}

export enum MessageStatus {
  Sent = 'sent',
  Delivered = 'delivered',
  Read = 'read',
  Failed = 'failed',
  Deleted = 'deleted',
  Queued = 'queued',
}

export type MessageBodyClass = {
  _id: string
};

export const messageClassId: string = '5e7ba9460fc22673073624d3';

export const TextMessageBodyClass: MessageBodyClass = {
  _id: '5e7baa930fc2267307362efd'
};

export const MediaMessageBodyClass: MessageBodyClass = {
  _id: '5e7babd80fc22673073633c9'
};

export const VideoConferenceMessageBodyClass: MessageBodyClass = {
  _id: '5f3e6829487dd1589e8ff45b'
};

export const LocationMessageBodyClass: MessageBodyClass = {
  _id: '5e82089d9f864e38464166bc'
};

export const TemplateMessageBodyClass: MessageBodyClass = {
  _id: '5e7c9bca0fc226730738b5e8'
};

export const ContactsMessageBodyClass: MessageBodyClass = {
  _id: '5e8207cc9f864e38464157dc'
};

export const MessageBodyTypeClass: {
  [Type in MessageType]: MessageBodyClass
} = {
  [MessageType.Audio]: MediaMessageBodyClass,
  [MessageType.Contacts]: ContactsMessageBodyClass,
  [MessageType.Document]: MediaMessageBodyClass,
  [MessageType.Hsm]: TemplateMessageBodyClass,
  [MessageType.Image]: MediaMessageBodyClass,
  [MessageType.Location]: LocationMessageBodyClass,
  [MessageType.Text]: TextMessageBodyClass,
  [MessageType.Video]: MediaMessageBodyClass,
  [MessageType.VideoConference]: VideoConferenceMessageBodyClass,
  [MessageType.Sticker]: MediaMessageBodyClass,
  [MessageType.Internal]: TextMessageBodyClass,
};

export interface BaseMessage {
  _id?: string;
  date: string | Date;
  from: CommunicationTool;
  chat?: Chat;
  type: MessageType;
  body: any;
  cancelToken?: CancelTokenSource;
  status: MessageStatus;
  error?: string;
  sendHash?: string;
  customKeyboards?: CustomKeyboard[];
  loading?: boolean;
  _lastUpdateDate?: string | Date;
}

export interface TextMessage extends BaseMessage {
  type: MessageType.Text | MessageType.Internal;
  body: {
    _class?: MessageBodyClass,
    body: string,
    preview_url?: boolean,
    payload?: string
  }
}

export interface MediaMessage extends BaseMessage {
  type: MessageType.Audio | MessageType.Document | MessageType.Image | MessageType.Video | MessageType.Sticker;
  body: {
    _class?: MessageBodyClass,
    type?: MessageType,
    file: FileBean,
    caption?: string,
    filename?: string
  }
}

export interface VideoConferenceMessage extends BaseMessage {
  type: MessageType.VideoConference;
  body: {
    _class?: MessageBodyClass,
    videoConference?: any;
    status?: "accepted" | "recorded" | "refused";
    link: string;
  }
}

export interface LocationMessage extends BaseMessage {
  type: MessageType.Location;
  body: {
    _class?: MessageBodyClass,
    coordinates: {
      type: 'Point',
      coordinates: number[]
    },
    name?: string,
    address?: string
  }
}

export interface ContactsMessage extends BaseMessage {
  type: MessageType.Contacts;
  body: {
    _class?: MessageBodyClass,
    customers: any[]
  }
}

export interface TemplateMessage extends BaseMessage {
  type: MessageType.Hsm;
  body: {
    _class?: MessageBodyClass,
    template: {
      _id: string;
    },
    preview?: string,
  }
}

export type Message = TextMessage | MediaMessage | VideoConferenceMessage | LocationMessage | ContactsMessage | TemplateMessage;

export default ChatAPI;